import React from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Avatar,
  useTheme,
  Button,
  Divider,
  Chip,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/EmailOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PhoneIcon from "@mui/icons-material/Emergency";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import { motion } from "framer-motion";

const Support = () => {
  const theme = useTheme();

  const generalContacts = [
    {
      icon: <EmailIcon fontSize="large" />,
      title: "Email Support",
      value: "support@kozyclan.com",
      action: "mailto:support@kozyclan.com",
      color: theme.palette.primary.main,
    },
    {
      icon: <WhatsAppIcon fontSize="large" />,
      title: "Whatsapp Chat",
      value: "+91-9650073540",
      action: "https://wa.me/919650073540",
      color: "#25D366",
    },
  ];

  const emergencyContact = {
    icon: <PhoneIcon fontSize="large" />,
    title: "24/7 Emergency Support",
    value: "+91-9650073540",
    action: "tel:+919650073540",
    color: theme.palette.error.main,
  };

  return (
    <Container maxWidth="md" sx={{ py: 2 }}>
      <Box textAlign="center" mb={3}>
        <Avatar
          sx={{
            bgcolor: theme.palette.primary.light,
            width: 80,
            height: 80,
            mb: 3,
            marginX: "auto",
          }}
        >
          <SupportAgentIcon sx={{ fontSize: 40 }} />
        </Avatar>

        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 800,
            color: theme.palette.primary.main,
            letterSpacing: "-0.5px",
          }}
        >
          Kozyclan Support
        </Typography>

        <Typography
          variant="h6"
          color="text.secondary"
          sx={{
            maxWidth: 600,
            margin: "0 auto",
            lineHeight: 1.6,
          }}
        >
          Choose the appropriate support channel based on your needs
        </Typography>
      </Box>

      {/* General Support Section */}
      <Box mb={8}>
        <Typography
          variant="h5"
          component="h4"
          gutterBottom
          sx={{
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2,
          }}
        >
          General Support : For general queries or assistance, feel free to
          reach out:
        </Typography>

        <Grid container spacing={4}>
          {generalContacts.map((method, index) => (
            <Grid item xs={12} md={6} key={index}>
              <motion.div whileHover={{ scale: 1.02 }}>
                <Card
                  component="a"
                  href={method.action}
                  target={method.title === "WhatsApp Chat" ? "_blank" : "_self"}
                  sx={{
                    textDecoration: "none",
                    borderRadius: 4,
                    boxShadow: theme.shadows[3],
                    "&:hover": {
                      boxShadow: theme.shadows[6],
                    },
                  }}
                >
                  <CardContent sx={{ textAlign: "center", py: 4 }}>
                    <Avatar
                      sx={{
                        bgcolor: method.color + "15",
                        width: 60,
                        height: 60,
                        marginX: "auto",
                        mb: 2,
                      }}
                    >
                      {React.cloneElement(method.icon, {
                        sx: { color: method.color },
                      })}
                    </Avatar>

                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      {method.title}
                    </Typography>

                    <Typography
                      variant="body1"
                      color="text.primary"
                      sx={{
                        fontWeight: 500,
                        mb: 2,
                      }}
                    >
                      {method.value}
                    </Typography>

                    <Button
                      variant="outlined"
                      sx={{
                        borderColor: method.color,
                        color: method.color,
                        "&:hover": {
                          bgcolor: method.color + "10",
                          borderColor: method.color,
                        },
                      }}
                    >
                      Contact Now
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Emergency Support Section */}
      <Box>
        <Typography
          variant="h5"
          component="h2"
          gutterBottom
          sx={{
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 4,
            color: theme.palette.error.main,
          }}
        >
          <Divider sx={{ flexGrow: 1, borderColor: "error.main" }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PhoneIcon sx={{ fontSize: 32 }} />
            Emergency Support
          </Box>
          <Divider sx={{ flexGrow: 1, borderColor: "error.main" }} />
        </Typography>

        <motion.div
          whileHover={{ scale: 1.02 }}
          style={{ display: "flex", justifyContent: "center" }}
        >
          <Card
            component="a"
            href={emergencyContact.action}
            sx={{
              textDecoration: "none",
              borderRadius: 4,
              border: `2px solid ${theme.palette.error.main}`,
              width: "100%",
              maxWidth: 600,
              boxShadow: `0 8px 32px ${theme.palette.error.light}30`,
              "&:hover": {
                boxShadow: `0 8px 32px ${theme.palette.error.light}50`,
              },
            }}
          >
            <CardContent sx={{ textAlign: "center", py: 4 }}>
              <Avatar
                sx={{
                  bgcolor: emergencyContact.color + "15",
                  width: 80,
                  height: 80,
                  marginX: "auto",
                  mb: 3,
                }}
              >
                {React.cloneElement(emergencyContact.icon, {
                  sx: {
                    color: emergencyContact.color,
                    fontSize: 40,
                  },
                })}
              </Avatar>

              <Chip
                label="24/7 Availability"
                color="error"
                sx={{ mb: 2, fontWeight: 600 }}
              />

              <Typography variant="h4" gutterBottom sx={{ fontWeight: 800 }}>
                {emergencyContact.value}
              </Typography>

              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  fontWeight: 500,
                  mb: 3,
                }}
              >
                Immediate assistance for urgent issues
              </Typography>

              <Button
                variant="contained"
                color="error"
                size="large"
                startIcon={<PhoneIcon />}
                sx={{
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                }}
              >
                Call Now
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </Box>

      <Box textAlign="center" mt={6}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
          </svg>
          General support response time: 1-2 business hours
        </Typography>
      </Box>
    </Container>
  );
};

export default Support;
